import { z } from 'zod';
import { diffAgents } from '../agent-diff/diff-agents';
import { agentChangeSchema, agentChangeSectionSchema } from './agent-change-schemas';
import { shownAgentChanges } from './shown-agent-changes';
import { optionalAgentIdSchema } from './agent-tool-schemas';
// What changed in an agent between two points, and version by version in between, as Via's compare card shows it.
/** Versions in between compared one by one; older ones are counted. */
export const VERSIONS_COMPARED = 10;
const endpointSchema = z.object({
    ref: z.string().describe('What was asked for, such as live, editor or a date'),
    name: z.string().describe('How the card names it, such as v12, Saved draft or Unsaved changes'),
    versionId: z.string().optional().describe('Missing for unsaved changes'),
    publishedAt: z.string().optional()
});
const stepSchema = z.object({
    versionId: z.string().optional(),
    fromVersionId: z.string().optional().describe('The version this step is compared with'),
    name: z.string(),
    publishedAt: z.string().optional(),
    publishedBy: z.string().optional(),
    labels: z.array(z.string()),
    changeCount: z.number(),
    sections: z
        .array(z.object({
        title: z.string(),
        changes: z.array(z.object({ kind: agentChangeSchema.shape.kind, noun: z.string(), label: z.string() }))
    }))
        .describe('What this step changed on its own, from the step before it')
});
const refDescription = "live (the latest published version), saved (the saved draft), editor (the agent as its editor holds it now, unsaved edits included; only while it's open), a versionId (from get_agent_deployment, or a conversation's agentVersionId), or a date like 2026-09-22 for the version that was live at the end of that day";
export const compareAgentVersionsToolSchema = {
    name: 'compare_agent_versions',
    description: "Show what changed in an agent between two points, as a card the user opens a step at a time: the number of changes by area, then each change, then its words before and after, with Atlas's full view and the Versions tab a click away. Between points in time it also lists each version published in between with what that version changed, so the user can see the changes one by one or all together. Use it when the user asks what changed, what's different from what's live or from last week, what publishing would change, or to review what you changed. The card holds the detail: reply with the few changes that matter and why, never a list of all of them.",
    inputSchema: z.object({
        agentId: optionalAgentIdSchema,
        from: z.string().describe(`Where to compare from: ${refDescription}`),
        to: z.string().describe(`Where to compare to: ${refDescription}`)
    }),
    outputSchema: z.object({
        agentId: z.string(),
        agentName: z.string(),
        from: endpointSchema,
        to: endpointSchema,
        changeCount: z.number(),
        sections: z.array(agentChangeSectionSchema),
        textsLeftOut: z
            .number()
            .optional()
            .describe('Changes listed without their text, past what fits here; the Versions tab has them'),
        steps: z
            .array(stepSchema)
            .describe('From `from` to `to` one step at a time, oldest first: each version published in between, then `to`'),
        stepsLeftOut: z.number().optional().describe('Older steps counted but not compared one by one')
    })
};
const stepOf = (before, after) => {
    const diff = diffAgents(before.snapshot, after.snapshot);
    return {
        ...(after.versionId && { versionId: after.versionId }),
        ...(before.versionId && { fromVersionId: before.versionId }),
        name: after.name,
        ...(after.publishedAt && { publishedAt: after.publishedAt }),
        ...(after.publishedBy && { publishedBy: after.publishedBy }),
        labels: after.labels,
        changeCount: diff.changeCount,
        sections: diff.sections.map(({ title, changes }) => ({
            title,
            changes: changes.map(({ kind, noun, label }) => ({ kind, noun, label }))
        }))
    };
};
const endpoint = ({ ref, name, versionId, publishedAt }) => ({
    ref,
    name,
    ...(versionId && { versionId }),
    ...(publishedAt && { publishedAt })
});
/**
 * The comparison of `from` and `to`, with `between` the versions published in between, oldest first. When there are
 * more than VERSIONS_COMPARED of them, `between` holds the newest ones and the one before them, and `betweenLeftOut`
 * counts the rest.
 */
export const describeAgentComparison = ({ agentId, agentName, from, to, between, betweenLeftOut = 0 }) => {
    const diff = diffAgents(from.snapshot, to.snapshot);
    // With older versions left out, the first loaded one is only the base for the one after it
    const chain = betweenLeftOut > 0 ? [...between, to] : [from, ...between, to];
    return {
        agentId,
        agentName,
        from: endpoint(from),
        to: endpoint(to),
        changeCount: diff.changeCount,
        ...shownAgentChanges(diff),
        steps: chain.slice(1).map((state, index) => stepOf(chain[index], state)),
        ...(betweenLeftOut > 0 && { stepsLeftOut: betweenLeftOut + 1 })
    };
};
/** Versions as the Versions tab numbers them: the oldest published version is v1. */
export const versionName = (versions, versionId) => {
    const index = versions.findIndex((version) => version.versionId === versionId);
    return index === -1 ? undefined : `v${versions.length - index}`;
};
/**
 * The published version a date points at: the latest published by the end of that day, or by that moment when it has
 * a time. Versions are newest first.
 */
export const versionLiveAt = (versions, date) => {
    const hasTime = date.includes('T');
    const at = new Date(hasTime ? date : `${date}T23:59:59.999`).getTime();
    if (Number.isNaN(at))
        return undefined;
    return versions.find(({ deployedAt }) => deployedAt <= at);
};
//# sourceMappingURL=agent-changes.js.map